import { useContext, useEffect, useRef } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as SonnerToaster } from '@/components/ui/sonner';
import { GlobalContextProvider, GlobalContext } from '@/contexts/global-context';
import { PrivyAuthProvider } from '@/contexts/privy-provider';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { MainHeader } from '@/components/main-header';
import { MainFooter } from '@/components/main-footer';
import { MainSidebar } from '@/components/main-sidebar';
import { LoginPromptHost } from '@/components/login';
import { cn, isInAppWebView } from '@/lib/utils';
import { PostHogProvider } from '@posthog/react';
import { capturePageView } from '@/lib/analytics/posthog-events';
import '@radix-ui/themes/styles.css';
import '@/app/assets/styles/globals.css';
import '@/app/assets/styles/globals-mobile.css';
import '@/app/assets/styles/globals-antd.css';

// 根据路由决定 header/sidebar 的 props（与各页面原先传参保持一致）
function getLayoutProps(pathname: string) {
  const rawSegment = pathname.replace(/^\//, '').split('?')[0].split('/')[0] || '';
  const segment = rawSegment.startsWith('@') ? 'me' : rawSegment;
  const headerProps: { hideHeader?: boolean; hideSearch?: boolean; hideLive?: boolean; isFullScreen?: boolean; className?: string } = {};
  const sidebarProps: { currentPath?: string; open?: boolean; defaultOpen?: boolean } = { currentPath: segment };
  let hideSidebar = false;
  let showFooter = true;
  let showDvw = false;
  if (pathname.startsWith('/login') || pathname.startsWith('/app-download') || pathname === '/home') {
    return {
      headerProps: { hideHeader: true },
      sidebarProps: { ...sidebarProps, currentPath: 'discover' },
      hideSidebar: true,
      showFooter: false,
      showDvh: true,
    };
  }
  if (pathname.startsWith('/stream/tools')) {
    return {
      headerProps: { hideSearch: true },
      sidebarProps: { ...sidebarProps, currentPath: 'discover' },
      hideSidebar: true,
      showFooter: true,
    };
  }
  if (pathname.startsWith('/help') || pathname.startsWith('/exam')) {
    return { headerProps: { hideHeader: true }, sidebarProps: { ...sidebarProps, currentPath: 'discover' }, hideSidebar: true, showFooter: false };
  }
  if (pathname.startsWith('/live/detail') || pathname.startsWith('/live/mobile') || pathname.startsWith('/live/reserve')) {
    headerProps.isFullScreen = true;
    headerProps.className = 'border-b border-button-border/20';
  }
  if (pathname.startsWith('/line')) sidebarProps.currentPath = 'markets';
  if (pathname.startsWith('/referral')) headerProps.hideSearch = true;
  if (pathname.startsWith('/me/form') || pathname.startsWith('/twitter-callback') || pathname.startsWith('/live/mobile')) {
    headerProps.hideLive = true;
    hideSidebar = true;
    showFooter = false;
  }
  if (pathname.startsWith('/scan')) sidebarProps.open = false;
  if (pathname.startsWith('/perps')) {
    sidebarProps.defaultOpen = false;
    showDvw = true;
  }
  if (pathname.startsWith('/predict')) sidebarProps.currentPath = 'predict';
  if (pathname.startsWith('/creator-weekly')) {
    hideSidebar = true;
    showFooter = false;
  }
  if (pathname.startsWith('/referral')) sidebarProps.currentPath = 'referral';
  if (pathname.startsWith('/feedback')) sidebarProps.currentPath = 'follow';
  if (pathname.startsWith('/livecenter')) sidebarProps.currentPath = 'admin';
  if (pathname.startsWith('/live/detail') || pathname.startsWith('/live/mobile') || pathname.startsWith('/live/reserve')) {
    showFooter = false;
  }
  return { headerProps, sidebarProps, hideSidebar, showFooter, showDvw };
}

function AppLayout() {
  const { langReady } = useContext(GlobalContext);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const lastTrackedPath = useRef('');

  useEffect(() => {
    if (!langReady) return;
    const qs = searchParams.toString();
    const path = qs ? `${pathname}?${qs}` : pathname;
    if (path === lastTrackedPath.current) return;
    lastTrackedPath.current = path;
    const options = {
      path: pathname,
      // 个人页用的tab，搜索页用的是type
      tab: searchParams.get('tab') || searchParams.get('type') || '',
      // 资产页用的accountType，tab是合约的页面区分
      accountType: searchParams.get('accountType') || '',
    };
    capturePageView(options);
  }, [langReady, pathname, searchParams]);

  const rawCurrentPath = pathname === '/' ? searchParams.get('tab') || 'discover' : pathname.replace(/^\//, '').split('?')[0].split('/')[0] || '';
  const currentPath = rawCurrentPath.startsWith('@') ? 'me' : rawCurrentPath;
  const { headerProps, sidebarProps, hideSidebar, showFooter, showDvw, showDvh } = getLayoutProps(pathname);
  let noHeader = false; // 兼容之前hide但渲染组件
  // App WebView 环境下 creator-weekly 页面不需要 header
  if (isInAppWebView()) {
    noHeader = true;
  }
  const effectiveSidebar = {
    ...sidebarProps,
    currentPath: pathname === '/' ? currentPath : (sidebarProps.currentPath ?? currentPath),
  };
  return (
    <div className={`min-h-screen ${!langReady ? 'opacity-0' : ''}`}>
      {!noHeader && <MainHeader {...headerProps} />}
      <div className={cn('flex co-main', showFooter ? '' : 'co-main-full', showDvh ? 'co-main-dvh' : '', showDvw ? 'co-main-dvw' : '')}>
        {langReady && !hideSidebar && <MainSidebar {...effectiveSidebar} />}
        <div className="co-right-side">
          <Outlet />
        </div>
      </div>
      {showFooter && <MainFooter />}
      <LoginPromptHost />
    </div>
  );
}

const options = {
  api_host: 'https://analytics.billion.live',
  /** 关闭会话录制（与项目里手写 `posthog.init` 时的 `disable_session_recording` 一致） */
  // disable_session_recording: true,
  // 需要查看原始请求时候使用
  // disable_compression: true,
} as const;

export default function App() {
  return (
    <>
      <Toaster />
      <SonnerToaster position="top-center" />
      <PostHogProvider apiKey={'phc_W64meoStf28GqmqmCJVlilALaGjc2gqItMn3ENIR3EU'} options={options}>
        <PrivyAuthProvider>
          <GlobalContextProvider>
            <AppLayout />
          </GlobalContextProvider>
        </PrivyAuthProvider>
      </PostHogProvider>
    </>
  );
}
